import {
    DataSourceStudio,
    GeographyType,
    InputPolygon,
    InputViewBox,
    SubscriptionResponse,
} from "@biggeo/bg-server-lib/studio";
import {
    LogicOperator,
    MapTemplateDataset,
    RequestOptions,
    DataSourceConfiguration,
    DatabaseType,
    DatasetGeometryPoint,
    DatasetStyles,
    FilterItem,
} from "@biggeo/bg-server-lib/studio";
import { MapFilterCriteriaForm } from "../../filter-criteria/utils/utils";
import { MapModes } from "./map";
import { FunctionType } from "../../utils/type";

export type MapType = React.MutableRefObject<mapboxgl.Map | null>;
export type DrawType = React.MutableRefObject<MapboxDraw | null>;

export enum MapContextPolygonSource {
    drawnShape = "drawnShape",
    boundary = "boundary",
}

export type MapContextPolygon = {
    source: MapContextPolygonSource;
    id: string;
    feature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>;
    isSelected: boolean;
};

export type MapState = {
    id?: string;
    map: MapType;
    draw: DrawType;
    featureCollection?: GeoJSON.FeatureCollection<
        GeoJSON.Geometry,
        GeoJSON.GeoJsonProperties
    >;
    polygons: MapContextPolygon[];
    datasets: MapContextDataset[];
    modes: Record<MapModes, boolean>;
    functionType?: FunctionType;
    viewport?: Partial<{
        bounds: mapboxgl.LngLatBounds;
        center: mapboxgl.LngLat;
        view: InputViewBox;
        zoom: number;
    }>;
    styleUrl?: string;
};

export type MapContextFilter = MapFilterCriteriaForm & {
    id: string;
    visible: boolean;
    selected: boolean;
    disabled: boolean;
    viewed?: boolean;
    preview: boolean;
};

export type MapContextDatasetConfiguration = {
    isOpen: boolean;
    options: RequestOptions;
    showLevelSets: boolean;
    showPoints: boolean;
};

export type MapContextDatasetFilterItem = FilterItem & {
    id: string;
    filterId?: string;
};

export type MapContextDatasetFilter = {
    logicOperator: LogicOperator;
    filters: MapContextDatasetFilterItem[];
};

export type MapContextDataset = {
    dataSource: DataSourceStudio;
    mapTemplateDataset?: MapTemplateDataset;
    dataSourceConfiguration: DataSourceConfiguration;
    geographyType?: GeographyType;
    isSample: boolean;
    isSelected: boolean;
    isVisible: boolean;
    isGettingStyled: boolean;
    isTableViewed: boolean;
    tableOrder?: number;
    isLegendOpen: boolean;
    configuration: MapContextDatasetConfiguration;
    selectedRows: DatasetGeometryPoint[];
    pinnedRows: DatasetGeometryPoint[];
    filters: MapContextDatasetFilter;
    fkMarketplaceDatasetId?: string;
    organization?: string;
    image?: string;
    color?: string;
};

export type MapContextPreviewDataset = {
    id: string;
    dataSourceId: string;
    marketplaceDatasetId: string;
    tableName: string;
    column?: string;
    geometry: string[];
    type: DatabaseType;
    isVisible: boolean;
    isGettingStyled: boolean;
    styles?: Pick<DatasetStyles, "fill" | "dataAggregation">;
};

export type MapUpdateResponse = Partial<
    Record<string, SubscriptionResponse | null>
>;

export enum GeospatialSelectionType {
    viewport = "viewport",
    multipolygon = "multipolygon",
}

export enum SocketConnectionMessages {
    connected = "connected",
    disconnectedRetry = "disconnectedRetry",
    disconnected = "disconnected",
}

export type GeospatialViewport = {
    readonly _tag: GeospatialSelectionType.viewport;
    readonly viewport: InputViewBox;
};

export type GeospatialMultipolygon = {
    readonly _tag: GeospatialSelectionType.multipolygon;
    readonly multipolygon: InputPolygon[];
};

export type GeospatialSelection = GeospatialViewport | GeospatialMultipolygon;
